import { useEffect, useState } from 'react';
import { Card } from './ui';
import type { Transcription } from '../types';

interface TranscriptionProgressProps {
  transcription: Transcription;
}

export default function TranscriptionProgress({ transcription }: TranscriptionProgressProps) {
  const [elapsedTime, setElapsedTime] = useState(0);

  // 経過時間のカウントアップ
  useEffect(() => {
    if (transcription.status !== 'processing' && transcription.status !== 'pending') {
      return;
    }

    const startTime = new Date(transcription.created_at).getTime();
    const updateElapsed = () => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [transcription.created_at, transcription.status]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatRemainingTime = (seconds: number): string => {
    if (seconds <= 0) return '計算中...';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `約${mins}分${secs > 0 ? `${secs}秒` : ''}`;
    }
    return `約${secs}秒`;
  };

  // 音声の長さから予想時間を計算（音声長の約1/12）
  const estimatedTotalTime = transcription.audio_duration
    ? Math.ceil(transcription.audio_duration * 0.083)
    : null;

  const progress = transcription.progress ?? 0;
  const progressMessage = transcription.progress_message ?? '処理を準備しています...';
  const estimatedRemaining = transcription.estimated_remaining_seconds
    ?? (estimatedTotalTime ? Math.max(0, estimatedTotalTime - elapsedTime) : null);

  return (
    <Card role="status" aria-live="polite">
      <div className="text-center mb-8">
        {/* アニメーションアイコン */}
        <div className="relative inline-block mb-6">
          <div className="w-24 h-24 rounded-full border-4 border-gray-200 flex items-center justify-center">
            <div className="w-20 h-20 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-primary">{progress}%</span>
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-2">
          {transcription.status === 'pending' ? '処理待ち' : '書き起こし中'}
        </h2>
        <p className="text-gray-600 text-lg mb-4">{progressMessage}</p>
      </div>

      {/* プログレスバー */}
      <div
        className="mb-6"
        role="progressbar"
        aria-label="書き起こし進捗"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* 時間情報 */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">経過時間</div>
          <div className="text-2xl font-mono font-bold">{formatTime(elapsedTime)}</div>
        </div>
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">残り予想時間</div>
          <div className="text-2xl font-bold">
            {estimatedRemaining !== null ? formatRemainingTime(estimatedRemaining) : '計算中...'}
          </div>
        </div>
      </div>

      {/* ファイル情報 */}
      <div className="bg-info-bg border border-info/30 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <svg className="w-5 h-5 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
          <span className="font-medium text-info-fg">{transcription.audio_filename}</span>
        </div>
        {transcription.audio_duration && (
          <div className="text-sm text-info-fg">
            音声の長さ: {Math.floor(transcription.audio_duration / 60)}分{Math.floor(transcription.audio_duration % 60)}秒
          </div>
        )}
      </div>

      {/* ページ遷移に関する注意 */}
      <div className="bg-success-bg border border-success/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-success flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="font-bold text-success-fg mb-1">このページから離れても大丈夫です</h3>
            <p className="text-sm text-success-fg">
              書き起こし処理はサーバー上で継続されます。
              別のページに移動したり、ブラウザを閉じても処理は止まりません。
              完了後はダッシュボードから結果を確認できます。
            </p>
          </div>
        </div>
      </div>

      {/* 処理ステップ。区切りはサーバ側の進捗値と一致させる（transcription_tasks.py）:
          前処理〜20 → 書き起こし〜80 → 話者識別・用語補正〜85 → 出力生成〜95 → 後片付け〜100。
          「書き起こし」だけが 80% まで進むため、その後に何が走っているのかをステップとして明示する
          （これが無いと、音声を最後まで処理し終えたのにバーが 80% で止まって見える）。 */}
      <div className="mt-8">
        <h3 className="font-medium text-gray-700 mb-4">処理ステップ</h3>
        <div className="space-y-3">
          <ProcessStep
            step={1}
            label="音声ファイルの前処理"
            isActive={progress >= 0 && progress < 20}
            isComplete={progress >= 20}
          />
          <ProcessStep
            step={2}
            label="書き起こし"
            isActive={progress >= 20 && progress < 80}
            isComplete={progress >= 80}
          />
          <ProcessStep
            step={3}
            label="話者の識別と用語の補正"
            isActive={progress >= 80 && progress < 85}
            isComplete={progress >= 85}
          />
          <ProcessStep
            step={4}
            label="出力ファイルの生成"
            isActive={progress >= 85 && progress < 95}
            isComplete={progress >= 95}
          />
          <ProcessStep
            step={5}
            label="クリーンアップ"
            isActive={progress >= 95 && progress < 100}
            isComplete={progress >= 100}
          />
        </div>
      </div>
    </Card>
  );
}

interface ProcessStepProps {
  step: number;
  label: string;
  isActive: boolean;
  isComplete: boolean;
}

function ProcessStep({ step, label, isActive, isComplete }: ProcessStepProps) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${
      isActive ? 'bg-primary/10 border border-primary' :
      isComplete ? 'bg-success-bg' : 'bg-gray-50'
    }`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        isComplete ? 'bg-success text-white' :
        isActive ? 'bg-primary text-white' : 'bg-gray-300 text-gray-600'
      }`}>
        {isComplete ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          step
        )}
      </div>
      <span className={`font-medium ${
        isComplete ? 'text-success-fg' :
        isActive ? 'text-primary' : 'text-gray-500'
      }`}>
        {label}
        {isActive && <span className="ml-2 text-sm animate-pulse">処理中...</span>}
      </span>
    </div>
  );
}
